/*
Copyright © 2021 EPAM Systems, Inc. All Rights Reserved.
All information contained herein is, and remains the property of EPAM Systems, Inc.
and/or its suppliers and is protected by international intellectual property law.
Dissemination of this information or reproduction of this material is strictly
forbidden, unless prior written permission is obtained from EPAM Systems, Inc
*/
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    getDayNamesShort,
    getMonthName,
    getDateToISOStringWithTimeZoneOffset,
    formatDate,
    toDate,
    isDayNotPast, formatFullDateTimezone,
} from '../../utils';
import { useDays } from './utils';

import './calendar.scss';

const calendar = require('b_').with('custom-calendar');

const inCurrMonth = (currDate, date) => toDate(currDate).getMonth() === toDate(date).getMonth();

export const DatePicker = ({
    value, onChange, isVisible, highlightDates = true,
    highlightedDates = [],
}) => {
    const [ daysArr, firstDayInChosenMonth, setValue ] = useDays(value);

    useEffect(() => {
        setValue(value);
    }, [ setValue, value ]);

    const onArrowsHandler = (e) => {
        const month = new Date(firstDayInChosenMonth).getMonth() + Number(e);
        const newDate = new Date(firstDayInChosenMonth).setMonth(month, 1);
        setValue(newDate);
    };

    const onDateHandler = (el) => {
        const { innerHTML } = el.target;
        const newDate = new Date(new Date(firstDayInChosenMonth).getFullYear(), new Date(firstDayInChosenMonth).getMonth(), innerHTML).toISOString();
        const targetDate = getDateToISOStringWithTimeZoneOffset(newDate);
        onChange(targetDate);
    };

    return !daysArr ? null : (
        <div className={calendar({ is_visible: isVisible })}>
            <header className={calendar('header')}>
                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                <button type="button" className={calendar('button', { left: true })} onClick={() => onArrowsHandler(-1)}/>
                <span className={calendar('title')}>{`${getMonthName(firstDayInChosenMonth)} ${firstDayInChosenMonth.getFullYear()}`}</span>
                {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                <button type="button" className={calendar('button', { right: true })} onClick={() => onArrowsHandler(1)}/>
            </header>
            <div className={calendar('date-pick')}>
                <header className={calendar('week-area')}>
                    {getDayNamesShort().map(dayName => (
                        <h4 key={dayName} className={calendar('week')}>
                            {dayName}
                        </h4>
                    ))}
                </header>

                <section className={calendar('days-area')}>
                    {daysArr?.map((day) => {
                        const hasTimeslots = highlightedDates.includes(formatFullDateTimezone(day));
                        return (
                            <span
                                role="presentation"
                                key={toDate(day).toISOString()}
                                className={calendar('day', {
                                    disabled: !inCurrMonth(firstDayInChosenMonth, day) || !isDayNotPast(day) || (highlightDates && !hasTimeslots),
                                    active: formatDate(value) === formatDate(day),
                                    ...(!highlightDates
                                        ? { highlighted: formatDate(day) === formatDate(new Date()) }
                                        : {
                                            highlighted: formatDate(day) === formatDate(new Date()) || hasTimeslots,
                                            highlightedTimetable: hasTimeslots,
                                            white: !hasTimeslots,
                                        }),

                                })}
                                onClick={e => !!day && onDateHandler(e)}>
                                {day.getDate()}
                            </span>
                        );
                    })}
                </section>
            </div>
        </div>
    );
};

DatePicker.propTypes = {
    onChange: PropTypes.func.isRequired,
    value: PropTypes.oneOfType([PropTypes.string || PropTypes.instanceOf(Date)]),
    isVisible: PropTypes.bool,
};

DatePicker.defaultProps = {
    isVisible: true,
    value: null,
};
